import { Components, Theme } from "@mui/material/styles";
import { tokens } from "./tokens";

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: tokens.colors.neutral[50],
        fontFamily: tokens.fontFamily,
        fontSize: 15,
      },
      "#root": {
        minHeight: "100vh",
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      size: "small",
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        textTransform: "none",
        fontWeight: 600,
        padding: "4px 10px",
        minHeight: 28,
        fontSize: "0.875rem",
      },
      sizeSmall: {
        padding: "3px 8px",
        minHeight: 26,
        fontSize: "0.84375rem",
      },
      sizeMedium: {
        padding: "4px 10px",
        minHeight: 28,
      },
      sizeLarge: {
        padding: "6px 14px",
        minHeight: 32,
        fontSize: "0.9375rem",
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
      },
      sizeSmall: {
        padding: 4,
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundImage: "none",
        borderRadius: tokens.radius.md,
        border: `1px solid ${tokens.colors.neutral[200]}`,
        boxShadow: "none",
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.md,
        border: `1px solid ${tokens.colors.neutral[200]}`,
        boxShadow: "none",
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 12,
        "&:last-child": { paddingBottom: 12 },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: tokens.radius.sm,
          backgroundColor: "#FFFFFF",
          fontSize: "0.9375rem",
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        fontSize: "0.9375rem",
      },
      input: {
        padding: "6px 10px",
      },
      inputSizeSmall: {
        padding: "5px 8px",
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: "0.9375rem",
      },
      sizeSmall: {
        fontSize: "0.875rem",
      },
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: "small",
    },
  },
  MuiSelect: {
    defaultProps: {
      size: "small",
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        width: "100%",
        maxWidth: "100%",
        // Keep tables within the viewport — columns compress instead of scrolling
        overflowX: "hidden",
      },
    },
  },
  MuiTable: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        width: "100%",
        maxWidth: "100%",
        tableLayout: "fixed",
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: "5px 8px",
        fontSize: "0.8125rem",
        lineHeight: 1.3,
        // Fixed layout: let columns shrink; truncate long values
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      },
      head: {
        fontWeight: 650,
        fontSize: "0.8125rem",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        color: tokens.colors.neutral[600],
        backgroundColor: tokens.colors.neutral[100],
        borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
        padding: "5px 8px",
        lineHeight: 1.25,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      body: {
        borderBottom: `1px solid ${tokens.colors.neutral[100]}`,
      },
      sizeSmall: {
        padding: "4px 6px",
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:last-child td": {
          borderBottom: 0,
        },
        "&:hover": {
          backgroundColor: tokens.colors.neutral[50],
        },
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: ({ ownerState }) => ({
        fontWeight: 600,
        borderRadius: tokens.radius.sm,
        height: 22,
        fontSize: "0.875rem",
        ...(ownerState.variant === "filled" &&
          ownerState.color &&
          ownerState.color !== "default" && {
            color: "#FFFFFF",
            "& .MuiChip-label": { color: "#FFFFFF" },
            "& .MuiChip-icon": { color: "#FFFFFF" },
            "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.85)" },
          }),
      }),
      sizeSmall: {
        height: 20,
        fontSize: "0.75rem",
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        padding: "4px 10px",
        fontSize: "0.875rem",
      },
      standardSuccess: {
        backgroundColor: tokens.colors.success.light,
        color: tokens.colors.success.main,
      },
      standardError: {
        backgroundColor: tokens.colors.error.light,
        color: tokens.colors.error.main,
      },
      standardWarning: {
        backgroundColor: tokens.colors.warning.light,
        color: tokens.colors.warning.main,
      },
      standardInfo: {
        backgroundColor: tokens.colors.info.light,
        color: tokens.colors.info.main,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.md,
        border: `1px solid ${tokens.colors.neutral[200]}`,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: "10px 14px",
        fontSize: "1.0625rem",
        fontWeight: 650,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "12px 14px",
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: "8px 12px",
      },
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
        color: tokens.colors.neutral[800],
        borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
        borderRadius: 0,
        boxShadow: "none",
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        minHeight: tokens.shellHeaderHeight,
        "@media (min-width:0px)": {
          minHeight: tokens.shellHeaderHeight,
        },
      },
      gutters: {
        paddingLeft: 8,
        paddingRight: 8,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: `1px solid ${tokens.colors.neutral[200]}`,
        borderRadius: 0,
        boxShadow: "none",
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        margin: 0,
        padding: "5px 10px",
        minHeight: 32,
        "&.Mui-selected": {
          fontWeight: 600,
          borderRadius: 0,
        },
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: 0,
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 28,
        "& svg": { fontSize: 18 },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 600,
        minHeight: 36,
        padding: "6px 12px",
        fontSize: "0.875rem",
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 36,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: "0.9375rem",
        minHeight: 32,
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: "0.875rem",
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        fontSize: "0.875rem",
        minWidth: 28,
        height: 28,
      },
    },
  },
};
